import React, { FC } from 'react';
import { Typography } from 'antd';
import { Transaction } from '~/types';
import { useTranslation } from 'react-i18next';
import { AmountField, CopyButton, StatusField } from '~/components';
import styled from 'styled-components';
import { useDirection } from '~/hooks';

interface TransactionDetailsProps {
  transaction: Transaction;
}

const TransactionDetailsContainer = styled.div`
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
`;

const FlexRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
`;

const LabelText = styled(Typography.Text)`
  color: #a1a0ae;
`;

const ValueText = styled(Typography.Text)`
  font-weight: 500;
`;

const TransactionIdContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TransactionDetails: FC<TransactionDetailsProps> = ({ transaction }) => {
  const { t } = useTranslation('sendMoney');

  const [direction] = useDirection();
  return (
    <TransactionDetailsContainer>
      <Typography.Title level={4}>{t('transactionDetails')}</Typography.Title>
      <FlexRow>
        <LabelText>{t('status')}</LabelText>
        <StatusField data={transaction?.status} addLabel={false} />
      </FlexRow>
      <FlexRow>
        <LabelText>{t('amount')}</LabelText>
        <ValueText>
          <AmountField amount={transaction?.amount} />
        </ValueText>
      </FlexRow>
      {transaction?.fees &&
        transaction?.fees?.length > 0 &&
        transaction?.fees?.map((fee, index) =>
          !fee?.amount ? null : (
            <FlexRow key={index}>
              <LabelText>{fee?.description}</LabelText>
              <ValueText>
                <AmountField amount={fee?.amount} />
              </ValueText>
            </FlexRow>
          )
        )}
      <FlexRow>
        <LabelText>{t('totalAmount')}</LabelText>
        <ValueText>
          <AmountField amount={transaction?.totalAmount} />
        </ValueText>
      </FlexRow>
      <FlexRow>
        <LabelText>{t('paidTo')}</LabelText>
        <ValueText>{transaction?.recipient?.accountNumber}</ValueText>
      </FlexRow>
      <FlexRow>
        <LabelText>{t('transactionId')}</LabelText>
        <TransactionIdContainer>
          <ValueText
            style={{
              maxWidth: '150px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              direction: 'ltr',
              textAlign: direction === 'rtl' ? 'right' : undefined,
            }}
          >
            {transaction?.id}
          </ValueText>
          <CopyButton text={transaction?.id || ''} isIcon />
        </TransactionIdContainer>
      </FlexRow>
    </TransactionDetailsContainer>
  );
};

export default TransactionDetails;
